import { getCurrentMatchesGradient } from "@/helper/color";
import {
  getDateWithDay,
  getDayOfWeekShort,
  getMatchTimeLabel,
} from "@/helper/time";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import { useRouter } from "next/router";

import { BasicMatch } from "@/data/match";
import { getTeamDetailUrl } from "@/helper/urlHelper";
import { Season } from "@/models/Season";
import { MatchState } from "@prisma/client";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import DynLogo from "../../assets/Dyn_Logo.png";
import { LinkButton } from "../common/Button";
import { DocumentLink } from "../common/DocumentLink";
import { TeamImage, TeamImageProps } from "../common/Images";

interface GamedayMatchListProps {
  matches: BasicMatch[];
  selectedMatchIndex: number;
  baseUrl: string;
  season: Season;
}
export function GamedayMatchList({
  matches,
  selectedMatchIndex,
  baseUrl,
  season,
}: GamedayMatchListProps) {
  const selectedMatch = matches[selectedMatchIndex];
  const matchBefore = matches[selectedMatchIndex - 1];
  const matchAfter = matches[selectedMatchIndex + 1];
  // console.log({ selectedMatchIndex, selectedMatch, matches });
  const { t } = useTranslation();
  const { locale } = useRouter();
  const matchMeta = [
    selectedMatch.matchNumber ? `${t("Match")} ${selectedMatch.matchNumber}` : null,
    selectedMatch.tableNumber
      ? `${t("Table_Number")} ${selectedMatch.tableNumber}`
      : null,
  ]
    .filter(Boolean)
    .join(" | ");
  return (
    <div className="container container-padding-responsive">
      <div className=" lg:bg-bg-gray lg:rounded-lg lg:p-4">
        <div className="grid grid-cols-3 gap-1 px-4 py-4 lg:px-0 lg:grid-cols-5 lg:gap-16 ">
          {matches.map((match) => (
            <MatchCard
              key={match.id}
              match={match}
              active={selectedMatch?.id ? match.id === selectedMatch.id : false}
              baseUrl={baseUrl}
            />
          ))}
        </div>
        <div className="bg-white p-4 lg:rounded-lg ">
          <div className="flex w-full">
            <Link
              className={`max-lg:hidden border-[1px] border-gray-light rounded-md p-4 mx-4 mb-2 mt-12 flex items-center ${
                selectedMatchIndex === 0 && "pointer-events-none disabled"
              }`}
              href={matchBefore ? `${baseUrl}/${matchBefore.id}` : ""}
              aria-label="Previous Match"
            >
              <FiChevronLeft />
            </Link>
            <div className="w-full">
              <div className="grid grid-cols-3 text-gray-dark uppercase w-full p-2">
                <div className="">
                  {getDateWithDay(selectedMatch.timeStamp, locale)}
                </div>
                <div className="text-center">{matchMeta}</div>
                <TimeDetail
                  matchState={selectedMatch.matchState}
                  match={selectedMatch}
                  className="text-right"
                />
              </div>
              <div
                className={`flex flex-col w-full space-y-2 rounded bg-gradient-to-b p-2 ${getCurrentMatchesGradient(
                  selectedMatch.timeStamp,
                  "Finished",
                )}`}
              >
                {selectedMatch.homeTeam && (
                  <DocumentLink
                    className="flex justify-between"
                    href={getTeamDetailUrl({
                      league: "Bundesliga",
                      season,
                      teamId: selectedMatch.homeTeam.seasonTeamId,
                    })}
                  >
                    <SelectedMatchTeam team={selectedMatch.homeTeam} />
                    {selectedMatch.matchState !== "Inactive" && (
                      <div
                        className={`w-12 h-12 relative bg-gradient-to-b inline-flex items-center justify-center rounded text-white font-antonio font-bold text-xl lg:text-2xl
            ${getCurrentMatchesGradient(
              selectedMatch.timeStamp,
              selectedMatch.matchState,
            )}
            `}
                      >
                        {selectedMatch.homeGames}
                      </div>
                    )}
                  </DocumentLink>
                )}
                {selectedMatch.awayTeam && (
                  <DocumentLink
                    className="flex justify-between"
                    href={getTeamDetailUrl({
                      league: "Bundesliga",
                      season,
                      teamId: selectedMatch.awayTeam.seasonTeamId,
                    })}
                  >
                    <SelectedMatchTeam team={selectedMatch.awayTeam} />
                    {selectedMatch.matchState !== "Inactive" && (
                      <div
                        className={`w-12 h-12 relative bg-gradient-to-b inline-flex items-center justify-center rounded text-white font-antonio font-bold text-xl lg:text-2xl
            ${getCurrentMatchesGradient(
              selectedMatch.timeStamp,
              selectedMatch.matchState,
            )}
            `}
                      >
                        {selectedMatch.awayGames}
                      </div>
                    )}
                  </DocumentLink>
                )}
              </div>
              {selectedMatch.livestreamUrl && (
                <LinkButton
                  href={selectedMatch.livestreamUrl}
                  iconLeft={DynLogo}
                  color="dyn"
                  fullWidth
                  linkClassName="mt-4"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("DYN_MatchDetail")}
                </LinkButton>
              )}
            </div>

            <Link
              className={`max-lg:hidden border-[1px] border-gray-light rounded-md p-4 mx-4 mb-2 mt-12 flex items-center ${
                selectedMatchIndex === matches.length - 1 &&
                "pointer-events-none disabled"
              }`}
              href={matchAfter ? `${baseUrl}/${matchAfter.id}` : ""}
              aria-label="Next Match"
            >
              <FiChevronRight />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

interface MatchCardProps {
  match: BasicMatch;
  active: boolean;
  baseUrl: string;
}
function MatchCard({ match, baseUrl }: MatchCardProps) {
  const isInactive = match.matchState === "Inactive";

  const router = useRouter();
  const selected = router.query.match === match.id;
  const locale = router.locale;

  return (
    <Link
      href={`${baseUrl}/${match.id}`}
      className="relative inline-flex items-center justify-between sm:justify-center rounded font-roboto font-bold bg-white border border-gray-light py-2 px-1 overflow-hidden hover:shadow transition duration-300 gap-4"
    >
      <TeamImage team={match.homeTeam} size="sm" />
      {!isInactive ? (
        <div
          className={`inline-flex items-center space-x-1 text-white text-sm min-[321px]:text-base rounded-sm px-1 bg-gradient-to-b ${getCurrentMatchesGradient(
            match.timeStamp,
            match.matchState,
          )}`}
        >
          <div className="-mb-px">{match.homeGames}</div>
          <div className="h-3 w-px bg-white" />
          <div className="-mb-px">{match.awayGames}</div>
        </div>
      ) : (
        <div className="inline-flex items-center flex-col text-center text-text-dark">
          <div>{getDayOfWeekShort(match.timeStamp, locale)}</div>
          <div className="text-xs -mt-1">{getMatchTimeLabel(match, locale)}</div>
        </div>
      )}
      <TeamImage team={match.awayTeam} size="sm" />
      {selected && (
        <div
          className={`absolute w-full top-0 left-0 right-0 h-1  bg-gradient-to-r ${getCurrentMatchesGradient(
            match.timeStamp,
            match.matchState,
          )}`}
        />
      )}
    </Link>
  );
}

const SelectedMatchTeam = ({ team }: { team: TeamImageProps["team"] }) => {
  return (
    <div className="flex items-center space-x-2">
      <div className="p-1 bg-white rounded">
        <TeamImage team={team} size="lg" />
      </div>
      <div className="font-antonio font-bold uppercase text-white text-xl">
        {team?.name}
      </div>
    </div>
  );
};

interface TimeDetailProps {
  match: BasicMatch;
  matchState: MatchState;
  className?: string;
}
function TimeDetail({ matchState, match, className }: TimeDetailProps) {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const timeSuffix = t("Time_Suffix");
  switch (matchState) {
    case "Inactive":
      return (
        <div className={className}>
          {getMatchTimeLabel(match, locale)}
          {!match.isTimeToBeDefined && timeSuffix ? ` ${timeSuffix}` : ""}
        </div>
      );
    case "Active":
      return (
        <div
          className={`inline-flex bg-white rounded-sm p-1 font-antonio font-bold text-sm leading-none text-white  bg-gradient-to-b from-orange-first to-orange-last ${className}`}
        >
          LIVE
        </div>
      );
    case "Finished":
      return <div className={`uppercase ${className}`}>{t("Finished")}</div>;
  }
}
